import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, MailOpen, Mail } from "lucide-react";

function safeDate(iso) {
  try {
    return format(new Date(iso), "d MMMM yyyy 'à' HH:mm", { locale: fr });
  } catch {
    return "";
  }
}

export default function MessagesManager() {
  const [messages, setMessages] = useState([]);

  const load = () => api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (id) => {
    await api.patch(`/admin/messages/${id}/read`).catch(() => {});
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce message ?")) return;
    await api.delete(`/admin/messages/${id}`).catch(() => {});
    toast.success("Message supprimé");
    load();
  };

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div data-testid="messages-manager">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold uppercase text-[#1E1B18]">Messages reçus</h2>
        <p className="mt-1 text-sm text-stone-500">
          {unread > 0 ? `${unread} message${unread > 1 ? "s" : ""} non lu${unread > 1 ? "s" : ""}` : "Tous les messages sont lus"} — ils arrivent ici quand un visiteur remplit le formulaire de contact.
        </p>
      </div>
      {messages.length === 0 ? (
        <p className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
          Aucun message pour le moment.
        </p>
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <div
              key={m.id}
              data-testid={`message-card-${m.id}`}
              className={`rounded-xl border bg-white p-5 shadow-sm ${m.read ? "border-stone-200" : "border-[#B83227]"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-[#1E1B18]">
                    {m.name} <span className="font-normal text-stone-500">&lt;{m.email}&gt;</span>
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500">
                    {safeDate(m.created_at)}
                    {m.prestation && <> · Prestation : {m.prestation}</>}
                    {m.event_info && <> · {m.event_info}</>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleRead(m.id)}
                    data-testid={`message-toggle-read-${m.id}`}
                    className="inline-flex items-center gap-1 rounded border border-stone-300 px-3 py-1.5 text-xs font-bold uppercase text-stone-600 hover:border-stone-500"
                  >
                    {m.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                    {m.read ? "Non lu" : "Marquer lu"}
                  </button>
                  <button
                    onClick={() => remove(m.id)}
                    data-testid={`message-delete-${m.id}`}
                    className="inline-flex items-center gap-1 rounded bg-red-50 px-3 py-1.5 text-xs font-bold uppercase text-red-700 hover:bg-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </div>
              </div>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-stone-700">{m.message}</p>
              {!m.read && <span className="mt-3 inline-block rounded bg-[#B83227] px-2 py-0.5 text-[10px] font-bold uppercase text-white">Nouveau</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
